import { useQuery } from "@tanstack/react-query";

import httpClient from "@/config/axios";
import { Label } from "@/components/ui/label";

/**
 * 수면OFF 주기 상태 (읽기 전용).
 *
 * ★ 왜 별도 표시가 필요한가 — `schedule_entries` 에는 'N' 만 저장되고 N1~N15 연번이
 *   없다. 근무표를 아무리 봐도 "이 사람이 몇 번째 N 인지", "미부여분이 몇 건
 *   밀려 있는지" 를 알 수 없다. 그 상태는 `nurse_night_cycle` 에만 있다.
 * ★ 특히 대기(pending_sleep)는 **이번 달 근무표 어디에도 안 나온다.** 자리를 못 찾아
 *   다음 달로 넘어간 값이라, 여기서 안 보여주면 다음 달에 갑자기 늘어난 것으로만
 *   보인다.
 *
 * ★★ null 과 0 을 구분해 그린다. null = 그 달 마감본이 없거나 아직 계산 전,
 *    0 = 계산했고 대기 없음. null 을 0 으로 채우면 "기능이 안 돈 것" 과 "정상인데
 *    대기가 없는 것" 이 같아 보인다.
 */
type NightCycleRow = {
  readonly nurse_id: string;
  readonly name?: string | null;
  readonly seq_at_end?: number | null;
  readonly pending_sleep?: number | null;
  readonly sleep_off_count?: number | null;
};

type NightCycleResult = {
  readonly cycle?: number | null;
  readonly pending_total?: number;
  /**
   * anchor    = 그 달 마감본에서 굳힌 값. **그 달 끝** 상태.
   * projected = 아직 안 연 달이라 전월 근무표에서 계산한 값. **이 달 시작**(넘어온 대기).
   * none      = 전월도 근무표가 없어 낼 값이 없다.
   */
  readonly source?: "anchor" | "projected" | "none";
  readonly rows?: NightCycleRow[];
};

/** null/undefined 는 계산 전이라 "—". 0 은 실제 값이므로 그대로 그린다. */
function renderCount(v: number | null | undefined) {
  return v === null || v === undefined ? "—" : String(v);
}

export default function NightCycleStatus({
  nurseId,
  groupId,
  year,
  month,
}: {
  readonly nurseId: string;
  readonly groupId: string;
  readonly year: number;
  readonly month: number;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["NIGHT_CYCLE", groupId, year, month] as const,
    enabled: Boolean(groupId && nurseId),
    queryFn: async () => {
      // httpClient 는 인터셉터에서 response.data 를 벗겨 본문을 직접 돌려준다.
      const res = await httpClient.get<NightCycleResult>(
        `/nurse-period/night-cycle`,
        { params: { year, month, group_id: groupId } }
      );
      return res as unknown as NightCycleResult;
    },
  });

  if (isError) {
    return (
      <div className="rounded-lg border border-[#E4E7EC] bg-[#F8FAFC] px-4 py-3">
        <p className="text-xs text-[#667085]">
          수면OFF 주기 정보를 불러오지 못했습니다.
        </p>
      </div>
    );
  }

  const row = data?.rows?.find(
    (r) => String(r.nurse_id) === String(nurseId)
  );
  const pending = row?.pending_sleep ?? null;
  const hasPending = typeof pending === "number" && pending > 0;
  // ★ `source` 는 나중에 추가된 필드다. 프론트가 먼저 배포되면 구 백엔드 응답에
  //   이 값이 없는데, 그때 "none" 으로 떨어뜨리면 **값이 멀쩡히 있는데도**
  //   "근무표가 없어 계산 전" 안내가 뜬다. 값 유무로 추론해 메운다.
  //   ★ 0 도 값이다. `??`/truthy 로 판정하면 seq_at_end=0 이 "없음" 이 된다.
  //     세 필드를 모두 본다 — 하나만 채워져 오는 응답도 계산된 것이다.
  const hasAnyValue =
    row != null &&
    [row.seq_at_end, row.pending_sleep, row.sleep_off_count].some(
      (v) => v !== null && v !== undefined
    );
  const source: "anchor" | "projected" | "none" =
    data?.source ?? (hasAnyValue ? "anchor" : "none");
  // 아직 안 연 달은 전월에서 계산한 **예상**이다. 마감본 값과 같아 보이면
  // 확정된 수치로 오해한다.
  const projected = source === "projected";

  return (
    <div className="rounded-lg border border-[#E4E7EC] bg-white px-4 py-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-[#344054]">
              수면OFF 주기
            </Label>
            {projected && (
              <span className="rounded-full bg-[#EFF8FF] px-2 py-0.5 text-[11px] font-medium text-[#175CD3]">
                전월 기준 예상
              </span>
            )}
          </div>
          {typeof data?.cycle === "number" && (
            <span className="text-xs text-[#667085]">
              나이트 {data.cycle}회마다
            </span>
          )}
        </div>
        <p className="break-keep text-xs leading-5 text-[#667085]">
          근무표에는 N 연번이 없어 여기서만 확인할 수 있습니다. 대기는 자리를 못
          찾아 다음 달로 넘어간 수면OFF 입니다.
        </p>
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-2">
        <div className="rounded-md bg-[#F8FAFC] px-3 py-2">
          <dt className="text-xs text-[#667085]">N 연번</dt>
          <dd className="mt-0.5 text-sm font-semibold text-[#344054]">
            {isLoading ? "…" : renderCount(row?.seq_at_end)}
          </dd>
        </div>
        <div
          className={
            hasPending
              ? "rounded-md bg-[#FEF3F2] px-3 py-2"
              : "rounded-md bg-[#F8FAFC] px-3 py-2"
          }
        >
          <dt className="text-xs text-[#667085]">대기</dt>
          <dd
            className={
              hasPending
                ? "mt-0.5 text-sm font-semibold text-[#B42318]"
                : "mt-0.5 text-sm font-semibold text-[#344054]"
            }
          >
            {isLoading ? "…" : renderCount(pending)}
          </dd>
        </div>
        {/* ★ projected 에서 라벨만 바꿔 pending 을 다시 그리면, 바로 옆 "대기" 와
            같은 값이 다른 지표처럼 두 번 보인다. 아직 만들지 않은 달은 부여
            자체가 없으므로 "—" 로 둔다(sleep_off_count 가 null 로 온다). */}
        <div className="rounded-md bg-[#F8FAFC] px-3 py-2">
          <dt className="text-xs text-[#667085]">이 달 부여</dt>
          <dd className="mt-0.5 text-sm font-semibold text-[#344054]">
            {isLoading ? "…" : renderCount(row?.sleep_off_count)}
          </dd>
        </div>
      </dl>

      {!isLoading && projected && (
        <p className="mt-2 break-keep text-xs leading-5 text-[#667085]">
          이 달은 아직 근무표를 만들지 않아, 전월 근무표로 계산한 예상값입니다.
          근무표를 마감하면 확정 수치로 바뀝니다.
        </p>
      )}
      {!isLoading && source === "none" && (
        <p className="mt-2 break-keep text-xs leading-5 text-[#667085]">
          이 달과 전월 모두 근무표가 없어 아직 계산되지 않았습니다.
        </p>
      )}
    </div>
  );
}
